import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDeskInPlan } from '../../../hooks/use-desk-in-plan';
import { relabel } from '../../../../stays/wording';
import { getAccessDetails, saveAccessDetails } from '../../../../api/stays-guide-client';
import { StaysApiError } from '../../../../api/stays-client';
import {
  GUIDE_ACCESS_KEYS,
  GuideAccessKey,
  GuideAccessStatus,
  GuideAccessValues,
} from '../../../../interfaces/stays-guide';
import { AppButton } from '../../../button/app-button';
import { ClockNote } from '../../../clock-note/clock-note';
import { CountedTextarea } from '../../../counted-textarea/counted-textarea';
import { Field, FieldError, HelperText, Input, Label } from '../../../form-kit';
import { StatusPill } from '../../../status-pill/status-pill';
import { StateSwitch } from '../../../../motion';
import { FormSkeleton, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { formatDate } from '../../../../function-utils/locale';
import toast from '../../../../utils/toast';
import {
  Banner,
  BannerText,
  BannerTitle,
  FieldGrid,
  Form,
  Muted,
  Row,
  SaveBar,
  Section,
  Wide,
} from './guide-editor.styled';
import { accessWindowHelper, deviceClock, olderThan90Days } from './guide-model';

/**
 * THE ACCESS DETAILS TAB (plan S7.8; stays:manage only — the shell hides it
 * otherwise). The values are SECRETS:
 *   - they load only when this tab opens (GET access-details, no-store, audited)
 *     and live only in this component's state — unmounting drops them; nothing
 *     is cached, stored, logged, or put in an aria-label / title / data-*;
 *   - codes are type="text" with autoComplete off and spellCheck off — never
 *     type="password": the host must read what they typed.
 * The K-101 warning comes FIRST. "Last changed" is on the host's device clock
 * (R-T12), with its clock note.
 */

const EMPTY: GuideAccessValues = {
  building_code: '',
  building_entry_steps: '',
  floor: '',
  apartment_door: '',
  apartment_directions: '',
  lockbox_code: '',
  lockbox_instructions: '',
  wifi_name: '',
  wifi_password: '',
};

const FIELDS: ReadonlyArray<{ key: GuideAccessKey; label: string; area?: boolean; code?: boolean; wide?: boolean }> = [
  { key: 'building_code', label: 'Building code', code: true },
  { key: 'floor', label: 'Floor' },
  { key: 'building_entry_steps', label: 'Entry steps', area: true, wide: true },
  { key: 'apartment_door', label: 'Apartment door' },
  { key: 'apartment_directions', label: 'Directions to the door', area: true, wide: true },
  { key: 'lockbox_code', label: 'Lockbox code', code: true },
  { key: 'lockbox_instructions', label: 'Lockbox instructions', area: true, wide: true },
  { key: 'wifi_name', label: 'Wi-Fi name', code: true },
  { key: 'wifi_password', label: 'Wi-Fi password', code: true },
];

const idOf = (k: string) => `access-field-${k}`;

export interface AccessDetailsFormProps {
  listingId: string;
  revealMinutes: number;
  expiryMinutes: number;
  onSaved?: (status: GuideAccessStatus | null) => void;
}

export const AccessDetailsForm: React.FC<AccessDetailsFormProps> = ({ listingId, revealMinutes, expiryMinutes, onSaved }) => {
  const [view, setView] = useState<'loading' | 'error' | 'ready'>('loading');
  const [values, setValues] = useState<GuideAccessValues>(EMPTY);
  const [base, setBase] = useState<GuideAccessValues>(EMPTY);
  const [version, setVersion] = useState(0);
  const desk = useDeskInPlan();
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [stale, setStale] = useState<string | null>(null);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const alive = useRef(true);
  const seq = useRef(0);

  const read = useCallback(async () => {
    const mine = ++seq.current;
    setView('loading');
    setStale(null);
    try {
      const out = await getAccessDetails(listingId);
      if (!alive.current || mine !== seq.current) return;
      const next = { ...EMPTY };
      for (const k of GUIDE_ACCESS_KEYS) next[k] = out && out.access && typeof out.access[k] === 'string' ? out.access[k] : '';
      setValues(next);
      setBase(next);
      setVersion(out && Number.isInteger(out.version) ? out.version : 0);
      setUpdatedAt(out ? out.updated_at : null);
      setView('ready');
    } catch {
      if (alive.current && mine === seq.current) setView('error');
    }
  }, [listingId]);

  useEffect(() => {
    alive.current = true;
    void read();

    return () => {
      // Dropped on the way out: nothing of the values outlives the tab.
      alive.current = false;
      seq.current += 1;
    };
  }, [read]);

  useEffect(() => {
    if (!focusKey) return;
    const el = document.getElementById(idOf(focusKey));
    if (el) el.focus();
    setFocusKey(null);
  }, [focusKey]);

  const changed = GUIDE_ACCESS_KEYS.filter(k => values[k] !== base[k]);

  const save = async () => {
    if (busy) return;
    if (!changed.length) {
      setSaved(true);

      return;
    }
    setBusy(true);
    setErrors({});
    setStale(null);
    const body: Partial<GuideAccessValues> = {};
    for (const k of changed) body[k] = values[k];
    try {
      const out = await saveAccessDetails(listingId, { ...body, version });
      if (!alive.current) return;
      setVersion(out.version);
      setUpdatedAt(out.updated_at);
      setBase(values);
      setSaved(true);
      if (onSaved) onSaved(out.access_status);
      toast.success('Access details saved');
    } catch (err) {
      if (!alive.current) return;
      const e = err as StaysApiError;
      if (e && e.code === 'STALE_ACCESS_DETAILS') {
        setStale(refusalText(e, 'Someone saved these access details a moment ago. Reload to see their changes.'));
      } else if (e && e.code === 'CODE_ALREADY_IN_TEXT') {
        const field = e.field || '';
        const words = refusalText(e, "This code also appears in the guide's text. Remove it there first, then save.");
        if (field) {
          setErrors({ [field]: words });
          setFocusKey(field);
        } else toast.error(words);
      } else if (e && e.status === 400 && Object.keys(e.fields || {}).length) {
        setErrors(e.fields);
        const first = GUIDE_ACCESS_KEYS.find(k => e.fields[k]);
        if (first) setFocusKey(first);
        else toast.error(refusalText(e, 'Could not save the access details. Try again.'));
      } else {
        toast.error(refusalText(e, 'Could not save the access details. Try again.'));
      }
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const set = (k: GuideAccessKey, v: string) => {
    setSaved(false);
    setValues(cur => ({ ...cur, [k]: v }));
    if (errors[k]) setErrors(e => {
      const next = { ...e };
      delete next[k];

      return next;
    });
  };

  return (
    <Form
      noValidate
      aria-label="Access details"
      autoComplete="off"
      onSubmit={e => {
        e.preventDefault();
        void save();
      }}
    >
      <Banner $tone="warning" role="note" aria-labelledby="access-warning-title" data-testid="access-warning">
        <BannerTitle id="access-warning-title">Physical codes keep working</BannerTitle>
        <BannerText>
          Every guest who completes check-in sees these codes during their stay window. A code a guest has seen keeps
          working until you change it on the lock. Change physical codes regularly and after any concern. Codes for each
          stay, generated on programmable locks, arrive in a later release.
        </BannerText>
      </Banner>

      <StateSwitch state={view}>
        {view === 'loading' ? <FormSkeleton fields={4} label="access details" /> : null}
        {view === 'error' ? <LoadError message="Couldn't load the access details" onRetry={() => void read()} /> : null}
        {view === 'ready' ? (
          <Section aria-label="Door codes and Wi-Fi">
            {updatedAt ? (
              <Row data-testid="access-last-changed">
                <Muted>
                  Last changed{' '}
                  <span data-at={updatedAt}>{formatDate(updatedAt, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </Muted>
                {olderThan90Days(updatedAt) ? <StatusPill tone="warning">Changed over 90 days ago</StatusPill> : null}
                <ClockNote clock={deviceClock()} inline />
              </Row>
            ) : null}
            {stale ? (
              <Banner $tone="warning" role="alert">
                <BannerText>{stale}</BannerText>
                <Row>
                  <AppButton variant="secondary" size="compact" onClick={() => void read()}>
                    Reload
                  </AppButton>
                </Row>
              </Banner>
            ) : null}
            <FieldGrid>
              {FIELDS.map(f => {
                const field = f.area ? (
                  <Field key={f.key}>
                    <CountedTextarea
                      id={idOf(f.key)}
                      label={relabel(f.label, desk)}
                      value={values[f.key]}
                      max={2000}
                      rows={3}
                      spellCheck={false}
                      autoComplete="off"
                      hasError={!!errors[f.key]}
                      aria-describedby={errors[f.key] ? `${idOf(f.key)}-error` : undefined}
                      onChange={e => set(f.key, e.target.value)}
                    />
                    <FieldError id={`${idOf(f.key)}-error`} message={errors[f.key]} />
                  </Field>
                ) : (
                  <Field key={f.key}>
                    <Label htmlFor={idOf(f.key)}>{relabel(f.label, desk)}</Label>
                    <Input
                      id={idOf(f.key)}
                      type="text"
                      autoComplete="off"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      maxLength={200}
                      value={values[f.key]}
                      hasError={!!errors[f.key]}
                      aria-invalid={errors[f.key] ? true : undefined}
                      aria-describedby={errors[f.key] ? `${idOf(f.key)}-error` : undefined}
                      onChange={e => set(f.key, e.target.value)}
                    />
                    <FieldError id={`${idOf(f.key)}-error`} message={errors[f.key]} />
                  </Field>
                );

                return f.wide ? <Wide key={f.key}>{field}</Wide> : field;
              })}
            </FieldGrid>
            <SaveBar>
              <AppButton type="submit" variant="primary" loading={busy} loadingLabel="Saving…" success={saved && !changed.length} successLabel="Saved">
                Save access details
              </AppButton>
            </SaveBar>
            <HelperText>{accessWindowHelper(revealMinutes, expiryMinutes)}</HelperText>
          </Section>
        ) : null}
      </StateSwitch>
    </Form>
  );
};
